import { memo } from 'react';
import { ZoomIn, ZoomOut, RotateCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { FavoriteButton } from '@/components/FavoriteButton';
import type { FavoriteCachedData } from '@/types/favorites';

interface ViewerToolbarProps {
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  scale?: number;
  onScaleChange?: (scale: number) => void;
  onRotate?: () => void;
  fileId?: string;
  favoriteCachedData?: FavoriteCachedData;
}

function ViewerToolbarInner({
  currentPage,
  totalPages,
  onPageChange,
  scale,
  onScaleChange,
  onRotate,
  fileId,
  favoriteCachedData,
}: ViewerToolbarProps) {
  const hasPages = currentPage != null && totalPages != null && totalPages > 1 && onPageChange;
  const hasZoom = scale != null && onScaleChange;
  const hasFavorite = !!fileId && !!favoriteCachedData;

  if (!hasPages && !hasZoom && !onRotate && !hasFavorite) return null;

  return (
    <div className="bg-[#fcf4fc] px-3 py-1.5 flex items-center justify-center gap-2 shrink-0 border-b border-[#f0d2f0]/50">
      {hasFavorite && (
        <div className="bg-white rounded-full px-1 py-0.5 border border-[#e5bce5]/50 flex items-center">
          <FavoriteButton
            itemId={fileId}
            itemType="file"
            cachedData={favoriteCachedData}
            size="sm"
            className="text-[#8e4585] hover:text-red-400"
          />
        </div>
      )}

      {hasPages && (
        <div className="flex items-center gap-0.5 bg-white rounded-full px-1.5 py-0.5 border border-[#e5bce5]/50">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            className="p-1.5 text-[#8e4585] hover:bg-[#f8e8f8] active:bg-[#f0d2f0] rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-colors min-w-[28px] min-h-[28px] flex items-center justify-center"
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-[#5a3d58] text-xs font-medium min-w-[48px] text-center">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="p-1.5 text-[#8e4585] hover:bg-[#f8e8f8] active:bg-[#f0d2f0] rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-colors min-w-[28px] min-h-[28px] flex items-center justify-center"
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {hasZoom && (
        <div className="flex items-center gap-0.5 bg-white rounded-full px-1.5 py-0.5 border border-[#e5bce5]/50">
          <button
            onClick={() => onScaleChange(Math.max(0.5, scale - 0.25))}
            disabled={scale <= 0.5}
            className="p-1.5 text-[#8e4585] hover:bg-[#f8e8f8] active:bg-[#f0d2f0] rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-colors min-w-[28px] min-h-[28px] flex items-center justify-center"
            aria-label="Zoom out"
          >
            <ZoomOut size={14} />
          </button>
          <span className="text-[#5a3d58] text-xs font-medium min-w-[38px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={() => onScaleChange(Math.min(3, scale + 0.25))}
            disabled={scale >= 3}
            className="p-1.5 text-[#8e4585] hover:bg-[#f8e8f8] active:bg-[#f0d2f0] rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-colors min-w-[28px] min-h-[28px] flex items-center justify-center"
            aria-label="Zoom in"
          >
            <ZoomIn size={14} />
          </button>
        </div>
      )}

      {onRotate && (
        <button
          onClick={onRotate}
          className="p-1.5 bg-white text-[#8e4585] hover:bg-[#f8e8f8] active:bg-[#f0d2f0] rounded-full border border-[#e5bce5]/50 transition-colors min-w-[28px] min-h-[28px] flex items-center justify-center"
          aria-label="Rotate"
        >
          <RotateCw size={14} />
        </button>
      )}
    </div>
  );
}

export const ViewerToolbar = memo(ViewerToolbarInner);
